import { isDefined } from "@clipboard-health/util-ts";
import { useEffect } from "react";

import { APP_V2_APP_EVENTS } from "../lib/analytics/events";
import { logError } from "../lib/analytics/log";
import { useNextTwoDaysShifts } from "../redesign/WorkerShifts/api/useNextTwoDaysShifts";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { isShiftWithinLiveActivityTriggerTimeWindow } from "./isShiftWithinLiveActivityTriggerTimeWindow";
import { useLiveActivitiesConfig } from "./useLiveActivitiesConfig";
import { useStartLiveActivityMutation } from "./useStartLiveActivityMutation";

export function useCreateLiveActivityForUpcomingShift() {
  const worker = useDefinedWorker();
  const { mutateAsync: startLiveActivity } = useStartLiveActivityMutation();

  const { data: nextTwoDaysShiftsData } = useNextTwoDaysShifts(worker.tmz ?? "", {
    enabled: isDefined(worker.tmz),
  });

  const {
    liveActivityTriggerLeadTimeMinimumInMinutes,
    liveActivityTriggerLeadTimeMaximumInMinutes,
  } = useLiveActivitiesConfig();

  useEffect(() => {
    const startLiveActivityForUpcomingShift = async () => {
      if (nextTwoDaysShiftsData?.response.length === 0) {
        return;
      }

      const shiftEligibleForLiveActivity = nextTwoDaysShiftsData?.response.find((shift) =>
        isShiftWithinLiveActivityTriggerTimeWindow({
          shiftStart: shift.start,
          liveActivityTriggerLeadTimeMinimumInMinutes,
          liveActivityTriggerLeadTimeMaximumInMinutes,
        })
      );

      if (!isDefined(shiftEligibleForLiveActivity)) {
        return;
      }

      const liveActivityParams = {
        shift: {
          id: shiftEligibleForLiveActivity._id,
          start: shiftEligibleForLiveActivity.start,
          end: shiftEligibleForLiveActivity.end,
          targetArrivalTime: shiftEligibleForLiveActivity.computedArrival?.targetArrivalTime,
          qualification: shiftEligibleForLiveActivity.agentReq,
        },
        workplace: {
          id: shiftEligibleForLiveActivity.facilityId ?? "",
          name: shiftEligibleForLiveActivity.facility.name,
          address: {
            formattedAddress: shiftEligibleForLiveActivity.facility.fullAddress?.formatted ?? "",
            longitude: shiftEligibleForLiveActivity.facility.geoLocation.coordinates[0].toString(),
            latitude: shiftEligibleForLiveActivity.facility.geoLocation.coordinates[1].toString(),
          },
          timezone: shiftEligibleForLiveActivity.facility.tmz ?? "America/Los_Angeles",
          type: shiftEligibleForLiveActivity.facility.type,
        },
      };

      try {
        await startLiveActivity(liveActivityParams);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_FAILED_TO_START_ERROR, {
          error,
          metadata: {
            shiftId: shiftEligibleForLiveActivity._id,
          },
        });
      }
    };

    void startLiveActivityForUpcomingShift();
  }, [
    liveActivityTriggerLeadTimeMaximumInMinutes,
    liveActivityTriggerLeadTimeMinimumInMinutes,
    nextTwoDaysShiftsData,
    startLiveActivity,
  ]);
}
